import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Play, Pause, RotateCcw, RotateCw, Maximize, Minimize,
  FileText, Video, BookOpen, AlertCircle, Loader2, ArrowRight, Lock
} from 'lucide-react';
import { AnimatedIcon, IconMotion } from './AnimatedIcon';
import T from './T';

const EASE = [0.22, 1, 0.36, 1] as const;

interface GrammarStudyChoiceProps {
  onSelectMode: (mode: 'text' | 'video') => void;
  titleText?: string;
  topicTitle?: string;
  // Idiomas sem vídeo-aula gravada: o cartão de vídeo fica bloqueado com aviso "em breve"
  videoLocked?: boolean;
}

// Cartões da escolha de formato (design minimal v1): tom pastel, orbe com ícone animado e seta que avança no hover
const CHOICES: { mode: 'text' | 'video'; Icon: React.FC<any>; motion: IconMotion; tint: string; orb: string; label: string; hint: string; meta: string }[] = [
  { mode: 'text', Icon: FileText, motion: 'flip', tint: 'bg-tint-lavender', orb: 'bg-tintd-lavender', label: 'Texto', hint: 'Explicação em texto passo a passo', meta: 'leitura' },
  { mode: 'video', Icon: Video, motion: 'pop', tint: 'bg-tint-sage', orb: 'bg-ok', label: 'Vídeo', hint: 'Assistir à vídeo aula explicativa', meta: 'aula' },
];

export const GrammarChoiceScreen: React.FC<GrammarStudyChoiceProps> = ({
  onSelectMode,
  titleText = 'Escolha como quer estudar gramática:',
  topicTitle,
  videoLocked = false
}) => {
  const [hovered, setHovered] = useState<string | null>(null);
  return (
    <div className="flex flex-col justify-center min-h-full p-6 max-w-2xl w-full mx-auto my-auto">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }} className="mb-10">
        <span className="text-[11px] uppercase font-mono tracking-[0.14em] text-muted flex items-center gap-2">
          <AnimatedIcon motion="draw"><BookOpen size={13} strokeWidth={1.75} /></AnimatedIcon> lição
        </span>
        {topicTitle && (
          <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-[-0.03em] text-ink mt-3 leading-[1.05]">
            {topicTitle}<span className="text-primary">.</span>
          </h2>
        )}
        <p className="text-sm text-muted mt-3">{titleText}</p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
        {CHOICES.map(({ mode, Icon, motion: m, tint, orb, label, hint, meta }, i) => {
          const locked = videoLocked && mode === 'video';
          return (
          <motion.button
            key={mode}
            type="button"
            onClick={() => { if (!locked) onSelectMode(mode); }}
            disabled={locked}
            aria-disabled={locked}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.08 + i * 0.07, ease: EASE }}
            whileHover={locked ? undefined : { y: -3 }}
            whileTap={locked ? undefined : { scale: 0.98 }}
            onHoverStart={() => setHovered(mode)}
            onHoverEnd={() => setHovered(null)}
            className={`group relative ${tint} text-ink rounded-[28px] p-6 min-h-[200px] flex flex-col justify-between text-left overflow-hidden ${locked ? 'cursor-not-allowed opacity-60 grayscale' : 'cursor-pointer'}`}
          >
            <div className="flex items-start justify-between">
              <div className={`w-14 h-14 rounded-full ${orb} text-surface flex items-center justify-center`}>
                {locked
                  ? <Lock size={22} strokeWidth={1.75} />
                  : <AnimatedIcon motion={m} active={hovered === mode} playOnHover={false}><Icon size={24} strokeWidth={1.75} /></AnimatedIcon>}
              </div>
              {locked ? (
                <span className="text-[11px] uppercase font-mono tracking-[0.14em] px-2.5 py-1 rounded-full bg-ink/10 text-ink/70"><T k="ui.grammar.videoComingSoon" /></span>
              ) : (
                <span className="text-[11px] uppercase font-mono tracking-[0.14em] text-ink/50">0{i + 1} · {meta}</span>
              )}
            </div>
            <div className="flex items-end justify-between gap-4 mt-8">
              <div>
                <h3 className="font-display text-2xl font-medium tracking-[-0.02em]">{label}</h3>
                <p className="text-[13px] text-ink/60 mt-1">{locked ? <T k="ui.grammar.videoComingSoonHint" /> : hint}</p>
              </div>
              {!locked && (
                <span className="w-10 h-10 shrink-0 rounded-full bg-primary/[0.07] flex items-center justify-center transition-all duration-300 group-hover:bg-primary group-hover:text-slate-900">
                  <ArrowRight size={17} strokeWidth={1.75} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                </span>
              )}
            </div>
          </motion.button>
          );
        })}
      </div>
    </div>
  );
};

interface GrammarVideoPlayerProps {
  langCode: string;
  level: string;
  nodeId: string;
  onFinishOrStartQuiz: () => void;
  topicTitle?: string;
}

export const GrammarVideoPlayer: React.FC<GrammarVideoPlayerProps> = ({
  langCode,
  level,
  nodeId,
  onFinishOrStartQuiz,
  topicTitle
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [useFallbackUrl, setUseFallbackUrl] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Construct Video URL
  const langFolderMap: Record<string, string> = {
    en: 'english',
    it: 'italian',
    fr: 'french'
  };
  const langFolder = langFolderMap[langCode.toLowerCase()] || 'english';
  const levelKey = level.toLowerCase();
  
  let baseId = (nodeId || '').trim();
  if (baseId.endsWith('.json')) baseId = baseId.replace('.json', '');
  if (baseId.endsWith('.mp4')) baseId = baseId.replace('.mp4', '');

  let primaryUrl = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/grammar-lang/final-videos/${langFolder}/${levelKey}/${baseId}.mp4`;
  let fallbackUrl = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/grammar-lang/final-videos/${langFolder}/${levelKey}/${baseId}_explanation.mp4`;

  if (langCode === 'it' && !baseId.endsWith('_explanation')) {
    primaryUrl = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/grammar-lang/final-videos/${langFolder}/${levelKey}/${baseId}_explanation.mp4`;
    fallbackUrl = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/grammar-lang/final-videos/${langFolder}/${levelKey}/${baseId}.mp4`;
  }

  const currentVideoUrl = useFallbackUrl ? fallbackUrl : primaryUrl;

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
  };

  const handleRewind10 = () => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 10);
  };

  const handleForward10 = () => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = Math.min(videoRef.current.duration || 0, videoRef.current.currentTime + 10);
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen?.().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.().catch(() => {});
      setIsFullscreen(false);
    }
  };

  const formatTime = (seconds: number) => {
    if (!seconds || isNaN(seconds)) return '00:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: EASE }}
      className="flex flex-col justify-center h-full w-full max-w-3xl mx-auto p-4 sm:p-6 gap-6 overflow-y-auto custom-scrollbar"
    >
      {topicTitle && (
        <div>
          <span className="text-[11px] uppercase font-mono tracking-[0.14em] text-muted flex items-center gap-2">
            <AnimatedIcon motion="pop"><Video size={13} strokeWidth={1.75} /></AnimatedIcon> vídeo aula
          </span>
          <h3 className="font-display text-3xl sm:text-4xl font-medium tracking-[-0.03em] text-ink mt-2 leading-tight">
            {topicTitle}<span className="text-primary">.</span>
          </h3>
        </div>
      )}

      {/* Video Container */}
      <div
        ref={containerRef}
        className="relative w-full aspect-video bg-black rounded-[28px] overflow-hidden group ring-1 ring-black/[0.06] dark:ring-white/[0.06] flex items-center justify-center"
      >
        {isLoading && !hasError && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-xs z-10">
            <Loader2 className="w-10 h-10 animate-spin text-primary" />
          </div>
        )}

        {hasError ? (
          <div className="flex flex-col items-center justify-center text-center p-6 text-slate-400 gap-3">
            <AlertCircle size={36} className="text-bad" />
            <p className="text-xs font-bold"><T k="ui.grammar.videoError" /></p>
            <button
              type="button"
              onClick={onFinishOrStartQuiz}
              className="px-4 py-2 bg-primary text-slate-900 text-[13px] font-medium rounded-xl"
            >
              Ir para os exercícios
            </button>
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              src={currentVideoUrl}
              playsInline
              className="w-full h-full object-contain"
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onTimeUpdate={() => {
                if (videoRef.current) setCurrentTime(videoRef.current.currentTime);
              }}
              onLoadedMetadata={() => {
                if (videoRef.current) {
                  setDuration(videoRef.current.duration);
                  setIsLoading(false);
                }
              }}
              onError={() => {
                if (!useFallbackUrl) {
                  setUseFallbackUrl(true);
                } else {
                  setHasError(true);
                  setIsLoading(false);
                }
              }}
            />

            {/* Custom Overlay Controls */}
            <div className={`absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent transition-opacity duration-300 flex flex-col justify-end p-4 sm:p-5 z-20 ${isPlaying ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'}`}>
              {/* Progress Slider */}
              <input
                type="range"
                min={0}
                max={duration || 100}
                value={currentTime}
                onChange={(e) => {
                  const newTime = parseFloat(e.target.value);
                  setCurrentTime(newTime);
                  if (videoRef.current) videoRef.current.currentTime = newTime;
                }}
                className="w-full h-1 bg-white/25 rounded-full appearance-none cursor-pointer accent-[#F4F1EC] mb-4"
              />

              <div className="flex items-center justify-between text-white">
                <div className="flex items-center gap-2.5">
                  {/* Play / Pause */}
                  <button
                    type="button"
                    onClick={togglePlay}
                    className="w-11 h-11 bg-[#F4F1EC] text-[#1C2430] rounded-full flex items-center justify-center hover:scale-105 active:scale-95 transition-all cursor-pointer"
                  >
                    <AnimatedIcon motion="pop" active={isPlaying}>
                      {isPlaying ? <Pause size={17} strokeWidth={1.75} /> : <Play size={17} fill="currentColor" className="ml-0.5" />}
                    </AnimatedIcon>
                  </button>

                  {/* -10s */}
                  <button
                    type="button"
                    onClick={handleRewind10}
                    className="w-9 h-9 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white rounded-full transition-all flex items-center justify-center cursor-pointer"
                    title="-10 segundos"
                  >
                    <AnimatedIcon motion="spin"><RotateCcw size={15} strokeWidth={1.75} /></AnimatedIcon>
                  </button>

                  {/* +10s */}
                  <button
                    type="button"
                    onClick={handleForward10}
                    className="w-9 h-9 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white rounded-full transition-all flex items-center justify-center cursor-pointer"
                    title="+10 segundos"
                  >
                    <AnimatedIcon motion="spin"><RotateCw size={15} strokeWidth={1.75} /></AnimatedIcon>
                  </button>

                  <span className="text-[11px] font-mono tracking-[0.08em] opacity-75 ml-1">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                </div>

                {/* Fullscreen */}
                <button
                  type="button"
                  onClick={toggleFullscreen}
                  className="w-9 h-9 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white rounded-full flex items-center justify-center transition-all cursor-pointer"
                  title="Tela cheia"
                >
                  {isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Start Quiz Action Button */}
      <button
        type="button"
        onClick={onFinishOrStartQuiz}
        className="group self-center pl-6 pr-2 py-2 bg-primary text-slate-900 rounded-full font-medium text-sm hover:scale-[1.02] active:scale-95 transition-all flex items-center gap-4 cursor-pointer"
      >
        Ir para os exercícios
        <span className="w-9 h-9 rounded-full bg-surface/15 flex items-center justify-center">
          <ArrowRight size={16} strokeWidth={1.75} className="transition-transform duration-300 group-hover:translate-x-0.5" />
        </span>
      </button>
    </motion.div>
  );
};
